import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { passwordResetAPI } from '../../services/api';

const STEPS = {
  IDLE: 'idle',
  CONFIRM_EMAIL: 'confirm_email',
  SECRET_KEY: 'secret_key',
  ENTER_OTP: 'enter_otp',
  RESETTING: 'resetting',
  DONE: 'done',
  ERROR: 'error',
};

function displayNameFromEmail(email, fallback) {
  if (fallback?.trim()) return fallback.trim();
  const local = String(email || '').split('@')[0] || 'there';
  return local.replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function PasswordResetChat({
  prefilledEmail = '',
  displayName: displayNameProp = '',
  title = 'Password Reset Assistant',
  subtitle = 'Microsoft 365 only — not PowerDesk ticket login',
  avatarEmoji = '🔐',
  accentGradient = 'linear-gradient(135deg, #0ea5e9 0%, #3B5BDB 100%)',
}) {
  const [step, setStep] = useState(STEPS.IDLE);
  const [companyEmail, setCompanyEmail] = useState('');
  const [maskedPersonal, setMaskedPersonal] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [secretKeyRequired, setSecretKeyRequired] = useState(true);
  const [secretKey, setSecretKey] = useState('');
  const [secretKeyConfirm, setSecretKeyConfirm] = useState('');
  const [verifiedSecretKey, setVerifiedSecretKey] = useState('');
  const [emailInput, setEmailInput] = useState(() => String(prefilledEmail || '').trim());

  const startReset = useCallback(async (typedEmail) => {
    const email = String(typedEmail ?? emailInput).trim().toLowerCase();
    if (!email) {
      setError('Please enter your company email address.');
      setStep(STEPS.ERROR);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await passwordResetAPI.lookup(email);
      setCompanyEmail(data.companyEmail);
      setMaskedPersonal(data.maskedPersonalEmail);
      setDisplayName(data.displayName || displayNameFromEmail(data.companyEmail, displayNameProp));
      setSecretKeyRequired(data.secretKeyRequired !== false);
      setSecretKey('');
      setSecretKeyConfirm('');
      setVerifiedSecretKey('');
      setMessage('');
      setStep(STEPS.CONFIRM_EMAIL);
    } catch (err) {
      setError(err.message || 'Lookup failed');
      setStep(STEPS.ERROR);
    } finally {
      setLoading(false);
    }
  }, [emailInput, displayNameProp]);

  const goToSecretKeyStep = () => {
    setError('');
    setMessage('');
    if (!secretKeyRequired) {
      setError('Secret key is not configured for this account. Please contact IT support.');
      return;
    }
    setSecretKey('');
    setSecretKeyConfirm('');
    setStep(STEPS.SECRET_KEY);
  };

  const confirmSecretAndSendOtp = async () => {
    const key = secretKey.trim();
    const keyConfirm = secretKeyConfirm.trim();
    if (!key || !keyConfirm) {
      setError('Please enter and confirm your secret key.');
      return;
    }
    if (key !== keyConfirm) {
      setError('Secret key and confirmation do not match.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await passwordResetAPI.sendOtp(companyEmail, key);
      setVerifiedSecretKey(key);
      setMaskedPersonal(data.maskedPersonalEmail || maskedPersonal);
      setOtp('');
      setStep(STEPS.ENTER_OTP);
      setMessage(data.message || 'OTP sent to your personal email.');
    } catch (err) {
      setError(err.message || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!verifiedSecretKey) {
      setStep(STEPS.SECRET_KEY);
      setError('Please enter your secret key again to resend the code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await passwordResetAPI.sendOtp(companyEmail, verifiedSecretKey);
      setMessage(data.message || 'A new 6-digit code was sent to your personal email.');
    } catch (err) {
      setError(err.message || 'Could not resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpAndReset = async () => {
    const code = otp.replace(/\D/g, '');
    if (code.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setLoading(true);
    setError('');
    setStep(STEPS.RESETTING);
    try {
      const verify = await passwordResetAPI.verifyOtp(companyEmail, code);
      setResetToken(verify.resetToken);
      const result = await passwordResetAPI.execute(verify.resetToken);
      setMessage(result.message);
      setStep(STEPS.DONE);
    } catch (err) {
      setError(err.message || 'Verification failed');
      setStep(STEPS.ENTER_OTP);
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep(STEPS.IDLE);
    setCompanyEmail('');
    setMaskedPersonal('');
    setOtp('');
    setResetToken('');
    setMessage('');
    setError('');
    setEmailInput(String(prefilledEmail || '').trim());
    setSecretKey('');
    setSecretKeyConfirm('');
    setVerifiedSecretKey('');
    setSecretKeyRequired(true);
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: 'auto', maxHeight: 'inherit',
      background: '#F0F2F5', fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <div style={{
        background: '#FFFFFF', borderBottom: '1px solid #DDE1E7',
        padding: '0 20px', height: 60, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, background: accentGradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>{avatarEmoji}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#1A202C' }}>{title}</div>
            <div style={{ fontSize: 11, color: '#718096' }}>{subtitle}</div>
          </div>
        </div>
        {step !== STEPS.IDLE && (
          <button type="button" onClick={resetFlow} style={{
            border: '1px solid #DDE1E7', background: '#fff', borderRadius: 8,
            padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#718096',
          }}>Start over</button>
        )}
      </div>

      <div style={{
        flex: '0 1 auto', overflowY: 'auto', padding: '14px 18px 12px',
        maxHeight: 'calc(100vh - 180px)',
      }}>
        {step === STEPS.IDLE && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ textAlign: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{avatarEmoji}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1A202C' }}>Reset Microsoft 365 password</div>
              <div style={{ fontSize: 13, color: '#718096', marginTop: 6, lineHeight: 1.5 }}>
                For Outlook, Teams, and Azure AD — not your PowerDesk agent/admin login on the main sign-in page.
              </div>
              {prefilledEmail && prefilledEmail.toLowerCase() !== emailInput.toLowerCase() && (
                <div style={{ fontSize: 12, color: '#D97706', marginTop: 8, background: '#FFFBEB', padding: '8px 10px', borderRadius: 8 }}>
                  Portal sign-in email may differ from your O365 account — use the email registered in IT.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Company email</label>
              <input
                type="email"
                autoComplete="email"
                placeholder="e.g. media@victinet.com"
                value={emailInput}
                onChange={e => { setEmailInput(e.target.value); setError(''); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !loading && emailInput.trim()) {
                    e.preventDefault();
                    startReset(emailInput);
                  }
                }}
                style={{
                  width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                  border: '1.5px solid #DDE1E7', fontSize: 14, boxSizing: 'border-box',
                  background: '#fff',
                }}
              />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
                Must match the company email stored in employee security (Admin → Reset).
              </div>
            </div>
            <button
              type="button"
              disabled={loading || !emailInput.trim()}
              onClick={() => startReset(emailInput)}
              style={{
                width: '100%', padding: '12px 18px', borderRadius: 12, border: 'none',
                background: loading ? '#93C5FD' : 'var(--accent, #3B5BDB)', color: '#fff',
                fontSize: 14, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
              }}
            >
              {loading ? 'Looking up…' : '🔑 Reset my password'}
            </button>
          </motion.div>
        )}

        {step === STEPS.CONFIRM_EMAIL && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p style={{ fontSize: 15, color: '#1A202C', lineHeight: 1.6 }}>
              Hi <strong>{displayName}</strong>, welcome to Password Reset Assistant.
            </p>
            <p style={{ fontSize: 14, color: '#374151', marginTop: 16, lineHeight: 1.6 }}>
              Your registered <strong>company email</strong> is:
            </p>
            <div style={{
              marginTop: 10, padding: '14px 16px', background: '#EEF2FF', borderRadius: 12,
              border: '1.5px solid #C7D2FE', fontSize: 15, fontWeight: 700, color: '#3730A3',
              wordBreak: 'break-all',
            }}>
              {companyEmail}
            </div>
            <p style={{ fontSize: 13, color: '#6B7280', marginTop: 12 }}>
              After you confirm your email and enter your <strong>secret key</strong>, a 6-digit code will be sent to your personal email
              {maskedPersonal ? <> (<strong>{maskedPersonal}</strong>)</> : null}.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" disabled={loading} onClick={goToSecretKeyStep} style={btnPrimary(loading)}>
                Yes, this is correct
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep(STEPS.IDLE);
                  setError('');
                  setMessage('');
                }}
                style={btnSecondary()}
              >
                No — use different email
              </button>
            </div>
          </motion.div>
        )}

        {step === STEPS.SECRET_KEY && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              Enter the <strong>secret key</strong> registered for your account in IT (Admin → Reset).
            </p>
            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Secret key</label>
              <input
                type="password"
                autoComplete="off"
                value={secretKey}
                onChange={e => { setSecretKey(e.target.value); setError(''); }}
                placeholder="Enter secret key"
                style={{
                  width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                  border: '1.5px solid #DDE1E7', fontSize: 14, boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Confirm secret key</label>
              <input
                type="password"
                autoComplete="off"
                value={secretKeyConfirm}
                onChange={e => { setSecretKeyConfirm(e.target.value); setError(''); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !loading && secretKey.trim() && secretKeyConfirm.trim()) {
                    e.preventDefault();
                    confirmSecretAndSendOtp();
                  }
                }}
                placeholder="Re-enter secret key"
                style={{
                  width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                  border: '1.5px solid #DDE1E7', fontSize: 14, boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              type="button"
              disabled={loading || !secretKey.trim() || !secretKeyConfirm.trim()}
              onClick={confirmSecretAndSendOtp}
              style={{ ...btnPrimary(loading || !secretKey.trim() || !secretKeyConfirm.trim()), width: '100%', marginTop: 16 }}
            >
              {loading ? 'Verifying…' : 'Continue & send OTP'}
            </button>
            <button
              type="button"
              onClick={() => { setStep(STEPS.CONFIRM_EMAIL); setError(''); }}
              style={{ ...btnSecondary(), width: '100%', marginTop: 10 }}
            >
              Back
            </button>
          </motion.div>
        )}

        {step === STEPS.ENTER_OTP && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {message && (
              <p style={{ fontSize: 13, color: '#059669', background: '#ECFDF5', padding: 12, borderRadius: 10, marginBottom: 16 }}>
                {message}
              </p>
            )}
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              Enter the <strong>6-digit OTP</strong> sent to <strong>{maskedPersonal}</strong>.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={{
                width: '100%', marginTop: 14, padding: '14px', fontSize: 24, letterSpacing: '0.35em',
                textAlign: 'center', borderRadius: 12, border: '2px solid #C7D2FE', fontWeight: 700,
                boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              disabled={loading || otp.length !== 6}
              onClick={verifyOtpAndReset}
              style={{ ...btnPrimary(loading || otp.length !== 6), width: '100%', marginTop: 16 }}
            >
              {loading ? 'Verifying…' : 'Verify OTP & reset password'}
            </button>
            <button type="button" disabled={loading} onClick={resendOtp} style={{ ...btnSecondary(), width: '100%', marginTop: 10 }}>
              Resend OTP
            </button>
          </motion.div>
        )}

        {step === STEPS.RESETTING && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#6B7280' }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Resetting your password…</div>
            <div style={{ fontSize: 12, marginTop: 8 }}>Please wait.</div>
          </div>
        )}

        {step === STEPS.DONE && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>✅</div>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#059669', textAlign: 'center' }}>Password reset successful</p>
            <p style={{ fontSize: 14, color: '#374151', marginTop: 12, lineHeight: 1.6, textAlign: 'center' }}>
              {message}
            </p>
            <p style={{ fontSize: 13, color: '#6B7280', marginTop: 12, textAlign: 'center' }}>
              Check <strong>{maskedPersonal}</strong> for your temporary password.
              You must change it when you sign in to Microsoft 365.
            </p>
            <button type="button" onClick={resetFlow} style={{ ...btnPrimary(false), width: '100%', marginTop: 20 }}>
              Done
            </button>
          </motion.div>
        )}

        {step === STEPS.ERROR && error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, padding: 16 }}>
            <p style={{ fontSize: 14, color: '#DC2626', margin: 0 }}>{error}</p>
            <button type="button" onClick={resetFlow} style={{ ...btnSecondary(), marginTop: 12 }}>Try again</button>
          </div>
        )}

        {error && step !== STEPS.ERROR && step !== STEPS.DONE && (
          <p style={{ fontSize: 13, color: '#DC2626', marginTop: 12 }}>{error}</p>
        )}
      </div>
    </div>
  );
}

function btnPrimary(disabled) {
  return {
    flex: 1, padding: '11px 16px', borderRadius: 10, border: 'none',
    background: disabled ? '#93C5FD' : 'var(--accent, #3B5BDB)', color: '#fff',
    fontSize: 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function btnSecondary() {
  return {
    padding: '11px 16px', borderRadius: 10, border: '1px solid #DDE1E7',
    background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151',
  };
}
